import { Link, useLocation } from "react-router-dom";
import { Home, Calendar, Users, BarChart3, Settings } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * MobileBottomNav - Fixed bottom navigation for mobile devices
 * 
 * Features:
 * - Always visible at bottom of screen on mobile
 * - Touch-optimized with proper spacing
 * - Active state indicators
 * - Safe area insets for notched devices
 * - Smooth transitions
 */

interface NavItem {
  to: string;
  icon: React.ReactNode;
  label: string;
}

const navItems: NavItem[] = [
  { to: "/", icon: <Home className="h-5 w-5" />, label: "Home" },
  { to: "/meetings", icon: <Calendar className="h-5 w-5" />, label: "Meetings" },
  { to: "/participants", icon: <Users className="h-5 w-5" />, label: "Participants" },
  { to: "/analytics", icon: <BarChart3 className="h-5 w-5" />, label: "Analytics" },
  { to: "/settings", icon: <Settings className="h-5 w-5" />, label: "Settings" },
];

export function MobileBottomNav() {
  const location = useLocation();

  // Don't show on auth page
  if (location.pathname === "/auth") {
    return null;
  }

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-background/98 backdrop-blur-sm border-t border-border shadow-[0_-2px_16px_rgba(0,0,0,0.08)]"
      style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 0.25rem)" }}
      role="navigation"
      aria-label="Mobile navigation"
    >
      <div className="flex items-center justify-around h-16 px-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.to;

          return (
            <Link
              key={item.to}
              to={item.to}
              className={cn(
                "flex flex-col items-center justify-center gap-1.5 px-2 py-2 rounded-xl",
                "min-w-[68px] min-h-[56px]",
                "transition-all duration-200 ease-out touch-manipulation",
                "active:scale-95 active:opacity-75",
                isActive
                  ? "text-primary bg-primary/15 shadow-sm"
                  : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
              )}
              aria-current={isActive ? "page" : undefined}
              preventScrollReset
            >
              <span className={cn(
                "transition-all duration-200",
                isActive && "scale-110 drop-shadow-sm"
              )}>
                {item.icon}
              </span>
              <span className={cn(
                "text-[11px] font-medium transition-all duration-200 leading-tight",
                isActive && "font-semibold"
              )}>
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
